import * as ExcelJS from 'exceljs';
import { getEnergyUsageAnalysis } from '@/apis/report/energyUsageAnalysis';

// ────────────────────────────────────────────────
// Constants (mirrored from backend energy-formula.util.js)
// ────────────────────────────────────────────────
const GAS_KCAL = 10290;
const ELEC_KCAL = 2290;
const WATER_KCAL = 1000000;
const TOE_BASE = 10000000;
const KCAL_TO_KWH = 4184 * 2.777778e-7;
const PF_GAS = 1.1;
const PF_WATER = 0.728;
const PF_ELEC = 2.75;

const MONTHS = [
  '1월', '2월', '3월', '4월', '5월', '6월',
  '7월', '8월', '9월', '10월', '11월', '12월',
];

function safeSum(arr: number[]): number {
  if (!Array.isArray(arr)) return 0;
  return arr.reduce((s, v) => s + (Number(v) || 0), 0);
}

/**
 * Task 6: 에너지사용량 분석 — Excel Export
 *
 * Appends a new worksheet "에너지사용량 분석" to the workbook
 * containing the 3-year matrix, TOE, and Primary Energy results.
 */
export const exportReportEnergyUsage = async (
  workbook: ExcelJS.Workbook,
  inspectionAreaId: number
) => {
  const response = await getEnergyUsageAnalysis(inspectionAreaId);
  if (!response?.data) return; // No data saved yet — skip

  const { yearly_data, gross_floor_area, professional_opinion } = response.data;
  if (!yearly_data || Object.keys(yearly_data).length === 0) return;

  const years = Object.keys(yearly_data).sort();
  const floorArea = gross_floor_area || 1;

  const sheet = workbook.addWorksheet('에너지사용량 분석');

  // ── Styling helpers ──
  const headerFont: Partial<ExcelJS.Font> = { bold: true, size: 10 };
  const titleFont: Partial<ExcelJS.Font> = { bold: true, size: 14 };
  const borderThin: Partial<ExcelJS.Border> = { style: 'thin' };
  const allBorders: Partial<ExcelJS.Borders> = {
    top: borderThin,
    left: borderThin,
    bottom: borderThin,
    right: borderThin,
  };
  const centerAlign: Partial<ExcelJS.Alignment> = {
    horizontal: 'center',
    vertical: 'middle',
  };
  const rightAlign: Partial<ExcelJS.Alignment> = {
    horizontal: 'right',
    vertical: 'middle',
  };

  // Column widths
  sheet.columns = [
    { width: 8 },  // A: 월
    ...years.flatMap(() => [
      { width: 14 }, // Gas
      { width: 14 }, // Water
      { width: 14 }, // Elec
    ]),
  ];

  let row = 1;

  // ── Title ──
  const titleCell = sheet.getCell(row, 1);
  titleCell.value = '에너지 사용량 분석';
  titleCell.font = titleFont;
  row += 2;

  // ── Floor area ──
  sheet.getCell(row, 1).value = '연면적 (㎡):';
  sheet.getCell(row, 1).font = headerFont;
  sheet.getCell(row, 2).value = gross_floor_area || 0;
  row += 2;

  // ── Header row 1: years ──
  const headerRow1 = sheet.getRow(row);
  headerRow1.getCell(1).value = '월';
  headerRow1.getCell(1).font = headerFont;
  headerRow1.getCell(1).alignment = centerAlign;
  headerRow1.getCell(1).border = allBorders;

  years.forEach((y, idx) => {
    const startCol = 2 + idx * 3;
    sheet.mergeCells(row, startCol, row, startCol + 2);
    const cell = sheet.getCell(row, startCol);
    cell.value = `${y}년`;
    cell.font = headerFont;
    cell.alignment = centerAlign;
    cell.border = allBorders;
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE9F3FD' } };
  });
  row++;

  // ── Header row 2: types ──
  const headerRow2 = sheet.getRow(row);
  headerRow2.getCell(1).value = '';
  headerRow2.getCell(1).border = allBorders;

  const typeLabels = ['가스(N㎥)', '중온수(Gcal)', '전기(kWh)'];
  const typeColors = ['FFFFF3E0', 'FFE3F2FD', 'FFFFF8E1'];

  years.forEach((_, yIdx) => {
    typeLabels.forEach((label, tIdx) => {
      const col = 2 + yIdx * 3 + tIdx;
      const cell = headerRow2.getCell(col);
      cell.value = label;
      cell.font = { ...headerFont, size: 9 };
      cell.alignment = centerAlign;
      cell.border = allBorders;
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: typeColors[tIdx] } };
    });
  });
  row++;

  // ── Data rows (12 months) ──
  const dataStartRow = row;
  MONTHS.forEach((label, mIdx) => {
    const dataRow = sheet.getRow(row);
    dataRow.getCell(1).value = label;
    dataRow.getCell(1).alignment = centerAlign;
    dataRow.getCell(1).border = allBorders;

    years.forEach((y, yIdx) => {
      const yd = yearly_data[y];
      const types = ['gas', 'water', 'elec'] as const;
      types.forEach((type, tIdx) => {
        const col = 2 + yIdx * 3 + tIdx;
        const cell = dataRow.getCell(col);
        const val = yd?.[type]?.[mIdx] ?? 0;
        cell.value = val;
        cell.alignment = rightAlign;
        cell.border = allBorders;
        cell.numFmt = '#,##0.##';
      });
    });
    row++;
  });

  // ── Total row ──
  const totalRow = sheet.getRow(row);
  totalRow.getCell(1).value = '합계';
  totalRow.getCell(1).font = headerFont;
  totalRow.getCell(1).alignment = centerAlign;
  totalRow.getCell(1).border = allBorders;
  totalRow.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF5F5F5' } };

  years.forEach((y, yIdx) => {
    const yd = yearly_data[y];
    const totals = [safeSum(yd?.gas || []), safeSum(yd?.water || []), safeSum(yd?.elec || [])];
    totals.forEach((total, tIdx) => {
      const col = 2 + yIdx * 3 + tIdx;
      const cell = totalRow.getCell(col);
      cell.value = total;
      cell.font = headerFont;
      cell.alignment = rightAlign;
      cell.border = allBorders;
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF5F5F5' } };
      cell.numFmt = '#,##0.##';
    });
  });
  row += 2;

  // ── TOE Results ──
  sheet.getCell(row, 1).value = 'TOE (석유환산톤)';
  sheet.getCell(row, 1).font = titleFont;
  row++;

  const toeHeaderRow = sheet.getRow(row);
  ['', '가스', '중온수', '전기', '합계'].forEach((label, idx) => {
    const cell = toeHeaderRow.getCell(idx + 1);
    cell.value = label;
    cell.font = headerFont;
    cell.alignment = centerAlign;
    cell.border = allBorders;
  });
  row++;

  years.forEach((y) => {
    const yd = yearly_data[y];
    const gasTotal = safeSum(yd?.gas || []);
    const waterTotal = safeSum(yd?.water || []);
    const elecTotal = safeSum(yd?.elec || []);

    const gasToe = (gasTotal * GAS_KCAL) / TOE_BASE;
    const waterToe = (waterTotal * WATER_KCAL) / TOE_BASE;
    const elecToe = (elecTotal * ELEC_KCAL) / TOE_BASE;

    const dataRow = sheet.getRow(row);
    dataRow.getCell(1).value = `${y}년`;
    dataRow.getCell(1).font = headerFont;
    dataRow.getCell(1).alignment = centerAlign;
    dataRow.getCell(1).border = allBorders;

    [gasToe, waterToe, elecToe, gasToe + waterToe + elecToe].forEach((v, idx) => {
      const cell = dataRow.getCell(idx + 2);
      cell.value = v;
      cell.alignment = rightAlign;
      cell.border = allBorders;
      cell.numFmt = '#,##0.0000';
    });
    row++;
  });
  row++;

  // ── Primary Energy Results ──
  sheet.getCell(row, 1).value = '1차에너지 사용량 (kWh/㎡)';
  sheet.getCell(row, 1).font = titleFont;
  row++;

  const peHeaderRow = sheet.getRow(row);
  ['', '가스', '중온수', '전기', '합계'].forEach((label, idx) => {
    const cell = peHeaderRow.getCell(idx + 1);
    cell.value = label;
    cell.font = headerFont;
    cell.alignment = centerAlign;
    cell.border = allBorders;
  });
  row++;

  years.forEach((y) => {
    const yd = yearly_data[y];
    const gasTotal = safeSum(yd?.gas || []);
    const waterTotal = safeSum(yd?.water || []);
    const elecTotal = safeSum(yd?.elec || []);

    const gasBaseKwh = gasTotal * GAS_KCAL * KCAL_TO_KWH;
    const waterBaseKwh = waterTotal * WATER_KCAL * KCAL_TO_KWH;
    const elecBaseKwh = elecTotal;

    const pGas = (gasBaseKwh * PF_GAS) / floorArea;
    const pWater = (waterBaseKwh * PF_WATER) / floorArea;
    const pElec = (elecBaseKwh * PF_ELEC) / floorArea;

    const dataRow = sheet.getRow(row);
    dataRow.getCell(1).value = `${y}년`;
    dataRow.getCell(1).font = headerFont;
    dataRow.getCell(1).alignment = centerAlign;
    dataRow.getCell(1).border = allBorders;

    [pGas, pWater, pElec, pGas + pWater + pElec].forEach((v, idx) => {
      const cell = dataRow.getCell(idx + 2);
      cell.value = v;
      cell.alignment = rightAlign;
      cell.border = allBorders;
      cell.numFmt = '#,##0.00';
    });
    row++;
  });
  row++;

  // ── Professional Opinion ──
  if (professional_opinion) {
    sheet.getCell(row, 1).value = '전문가 의견';
    sheet.getCell(row, 1).font = headerFont;
    row++;
    sheet.mergeCells(row, 1, row + 3, 1 + years.length * 3);
    sheet.getCell(row, 1).value = professional_opinion;
    sheet.getCell(row, 1).alignment = { wrapText: true, vertical: 'top' };
  }
};
